import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Trash2, Save, Star, FileText, Image as ImageIcon, ChevronDown, ChevronUp, Phone, Mail, FileSpreadsheet, FileDown } from "lucide-react";
import { getApplications, updateApplication, deleteApplication, exportApplications } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STATUSES = ["Neu", "In Prüfung", "Einladung", "Abgelehnt", "Eingestellt"];
const STATUS_COLORS = {
  "Neu": "bg-blue-100 text-blue-700",
  "In Prüfung": "bg-amber-100 text-amber-700",
  "Einladung": "bg-violet-100 text-violet-700",
  "Abgelehnt": "bg-red-100 text-red-700",
  "Eingestellt": "bg-green-100 text-green-700",
};

export const AdminApplications = () => {
  const [apps, setApps] = useState(null);
  const [fStatus, setFStatus] = useState("");
  const [fPosition, setFPosition] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => {
    const params = {};
    if (fStatus) params.status = fStatus;
    if (fPosition) params.position = fPosition;
    getApplications(params).then(setApps).catch(() => setApps([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fStatus, fPosition]);

  const change = (id, k, v) => setApps((p) => p.map((a) => (a.id === id ? { ...a, [k]: v } : a)));
  const save = async (a) => {
    try { await updateApplication(a.id, { status: a.status, rating: a.rating, notes: a.notes }); toast.success("Gespeichert."); }
    catch { toast.error("Speichern fehlgeschlagen."); }
  };
  const remove = async (id) => {
    try { await deleteApplication(id); setApps((p) => p.filter((a) => a.id !== id)); toast.success("Bewerbung gelöscht."); }
    catch { toast.error("Löschen fehlgeschlagen."); }
  };

  const [exporting, setExporting] = useState("");
  const doExport = async (format) => {
    setExporting(format);
    try {
      const params = {};
      if (fStatus) params.status = fStatus;
      if (fPosition) params.position = fPosition;
      const blob = await exportApplications(format, params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bewerbungen.${format === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export fehlgeschlagen."); }
    finally { setExporting(""); }
  };

  if (!apps) return <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-wine" /></div>;

  const positions = [...new Set(apps.map((a) => a.position).filter(Boolean))];
  const inputCls = "bg-white border border-wine/15 rounded px-3 py-2 text-sm focus:border-wine focus:outline-none";
  const fileUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND_URL}${u}`);

  return (
    <div data-testid="admin-applications">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h3 className="font-serif text-2xl text-wine">Bewerbungen ({apps.length})</h3>
        <div className="flex flex-wrap gap-3">
          <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className={inputCls} data-testid="filter-status">
            <option value="">Alle Status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={fPosition} onChange={(e) => setFPosition(e.target.value)} className={inputCls} data-testid="filter-position">
            <option value="">Alle Stellen</option>
            {positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button onClick={() => doExport("xlsx")} disabled={!!exporting || apps.length === 0} data-testid="export-xlsx" className="inline-flex items-center gap-2 border border-green-700/40 text-green-700 hover:bg-green-700 hover:text-white px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors disabled:opacity-50">
            {exporting === "xlsx" ? <Loader2 size={14} className="animate-spin" /> : <FileSpreadsheet size={14} />} Excel
          </button>
          <button onClick={() => doExport("pdf")} disabled={!!exporting || apps.length === 0} data-testid="export-pdf" className="inline-flex items-center gap-2 border border-wine/40 text-wine hover:bg-wine hover:text-cream px-4 py-2 text-xs uppercase tracking-[0.12em] transition-colors disabled:opacity-50">
            {exporting === "pdf" ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} PDF
          </button>
        </div>
      </div>

      {apps.length === 0 && <p className="text-sm text-ink-soft">Keine Bewerbungen gefunden.</p>}

      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} className="border border-gold/20 bg-white/60" data-testid={`application-${a.id}`}>
            <button onClick={() => setOpenId(openId === a.id ? null : a.id)} className="w-full flex items-center justify-between gap-3 p-4 text-left">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-serif text-lg text-ink">{a.first_name} {a.last_name}</span>
                <span className="text-sm text-ink-muted">· {a.position}</span>
                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-1 rounded-full ${STATUS_COLORS[a.status] || "bg-gray-100 text-gray-600"}`}>{a.status}</span>
                <span className="flex items-center gap-0.5">{[1,2,3,4,5].map((n) => <Star key={n} size={12} className={n <= (a.rating || 0) ? "text-gold fill-gold" : "text-ink-muted/30"} />)}</span>
              </div>
              {openId === a.id ? <ChevronUp size={18} className="text-ink-muted shrink-0" /> : <ChevronDown size={18} className="text-ink-muted shrink-0" />}
            </button>

            {openId === a.id && (
              <div className="px-4 pb-5 border-t border-gold/10 pt-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-ink-soft">
                  <a href={`tel:${a.phone}`} className="flex items-center gap-2 hover:text-wine"><Phone size={14} className="text-gold" />{a.phone}</a>
                  <a href={`mailto:${a.email}`} className="flex items-center gap-2 hover:text-wine"><Mail size={14} className="text-gold" />{a.email}</a>
                  <p><span className="text-ink-muted">Beschäftigung:</span> {a.employment_type || "–"}</p>
                  <p><span className="text-ink-muted">Eintritt:</span> {a.earliest_start || "–"}</p>
                  <p><span className="text-ink-muted">Sprachen:</span> {a.languages || "–"}</p>
                  <p><span className="text-ink-muted">Führerschein:</span> {a.drivers_license ? "Ja" : "Nein"}</p>
                </div>
                {a.experience && <p className="text-sm text-ink-soft"><span className="text-ink-muted block text-xs uppercase tracking-[0.1em] mb-1">Berufserfahrung</span>{a.experience}</p>}
                {a.motivation && <p className="text-sm text-ink-soft"><span className="text-ink-muted block text-xs uppercase tracking-[0.1em] mb-1">Motivation</span>{a.motivation}</p>}

                <div className="flex flex-wrap gap-3">
                  {a.cv_url && <a href={fileUrl(a.cv_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-wine/30 text-wine px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-wine hover:text-cream transition-colors"><FileText size={14} /> Lebenslauf</a>}
                  {a.certificates_url && <a href={fileUrl(a.certificates_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-wine/30 text-wine px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-wine hover:text-cream transition-colors"><FileText size={14} /> Zeugnisse</a>}
                  {a.photo_url && <a href={fileUrl(a.photo_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-wine/30 text-wine px-4 py-2 text-xs uppercase tracking-[0.1em] hover:bg-wine hover:text-cream transition-colors"><ImageIcon size={14} /> Foto</a>}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-ink-muted mb-1 block">Status</label>
                    <select value={a.status} onChange={(e) => change(a.id, "status", e.target.value)} className={inputCls + " w-full"} data-testid={`app-status-${a.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.1em] text-ink-muted mb-1 block">Bewertung</label>
                    <div className="flex items-center gap-1 py-1.5">
                      {[1,2,3,4,5].map((n) => (
                        <button key={n} type="button" onClick={() => change(a.id, "rating", n === a.rating ? 0 : n)} data-testid={`app-rating-${a.id}-${n}`}>
                          <Star size={22} className={n <= (a.rating || 0) ? "text-gold fill-gold" : "text-ink-muted/40 hover:text-gold"} />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="text-xs uppercase tracking-[0.1em] text-ink-muted mb-1 block">Interne Notizen</label>
                  <textarea value={a.notes || ""} onChange={(e) => change(a.id, "notes", e.target.value)} rows={2} className={inputCls + " w-full resize-y"} data-testid={`app-notes-${a.id}`} />
                </div>

                <div className="flex items-center justify-between">
                  <button onClick={() => remove(a.id)} className="text-wine hover:text-wine-dark inline-flex items-center gap-1 text-xs uppercase tracking-[0.1em]" data-testid={`app-delete-${a.id}`}><Trash2 size={14} /> Löschen</button>
                  <button onClick={() => save(a)} className="inline-flex items-center gap-2 bg-gold text-ink px-5 py-2.5 text-xs uppercase tracking-[0.15em]" data-testid={`app-save-${a.id}`}><Save size={14} /> Speichern</button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
